// File: frontend/src/pages/SuperAdmin/components/SystemSettings/GeographyManagement.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrashAlt, 
  faPlus, 
  faSync, 
  faSearch,
  faTimes,
  faUpload,
  faGlobe,
  faLayerGroup,
  faMapMarkerAlt,
  faLink,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { geoService } from '../../../../../services/superAdmin/geoService';
import GeographyUpload from '../GeographyUpload';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './GeographyManagement.module.css';

// Set your Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || 'your-mapbox-token';

const GeographyManagement = ({ loading, setLoading, error, setError, success, setSuccess }) => {
  const { t } = useLanguage();
  const mapContainer = useRef(null);
  const map = useRef(null);
  
  // Geography Management
  const [geoTypes, setGeoTypes] = useState([
    'country',
    'province',
    'state',
    'county',
    'census_division',
    'census_subdivision',
    'electoral_district',
    'fsa',
    'custom'
  ]);
  const [selectedGeoType, setSelectedGeoType] = useState(null);
  const [geographies, setGeographies] = useState([]);
  const [filteredGeographies, setFilteredGeographies] = useState([]);
  const [selectedGeography, setSelectedGeography] = useState(null);
  const [childGeographies, setChildGeographies] = useState([]);
  const [editingGeography, setEditingGeography] = useState(false);
  const [newGeography, setNewGeography] = useState({
    geo_id: '',
    geo_type: '',
    geo_name: '',
    geo_name_fr: '',
    geo_code: '',
    parent_geo_id: 'NONE',
    properties: {}
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [geoJsonFile, setGeoJsonFile] = useState(null);
  const [geoJsonPreview, setGeoJsonPreview] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [parentOptions, setParentOptions] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const handleUploadComplete = (results) => {
    // Refresh the list after upload
    if (selectedGeoType) {
      fetchGeographiesByType(selectedGeoType);
    }
    
    // Show success message
    const successCount = results.filter(r => r.status === 'success').length;
    if (successCount > 0) {
      setSuccess(t('upload_success_message', { count: successCount }));
    }
  };

  // Fetch geographies by type
  const fetchGeographiesByType = async (type, token = null, append = false) => {
    if (!type) return;
    
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const options = {
        limit: 100
      };
      
      if (token) {
        options.next_token = token;
      }
      
      const response = await geoService.getGeographiesByType(type, options);
      
      // Extract geographies from the response
      const geographiesData = response.geography_entries || 
                            (response.data && response.data.geography_entries) || 
                            [];
      
      // Store next token for pagination if available
      if (response.next_token) {
        setNextToken(response.next_token);
        setHasMoreData(true);
      } else {
        setNextToken(null);
        setHasMoreData(false);
      }
      
      // Update state based on whether we're appending or replacing
      if (append) {
        setGeographies(prev => [...prev, ...geographiesData]);
        setFilteredGeographies(prev => [...prev, ...geographiesData]);
      } else {
        setGeographies(geographiesData);
        setFilteredGeographies(geographiesData);
      }
    } catch (err) {
      console.error('Error fetching geographies by type:', err);
      setError(t('failed_to_load_geographies'));
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };
  
  // Fetch geography details including children
  const fetchGeographyDetails = async (geoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await geoService.getGeographyById(geoId, true, true);
      
      // Extract geography and children from the response
      const geography = response.geography || 
                      (response.data && response.data.geography);
      
      const children = response.children || 
                     (response.data && response.data.children) || 
                     [];
      
      setSelectedGeography(geography);
      setChildGeographies(children);
      
      // Initialize map if boundary data is available
      if (geography && geography.boundary_geojson && mapContainer.current) {
        initializeMap(geography.boundary_geojson);
      }
    } catch (err) {
      console.error('Error fetching geography details:', err);
      setError(t('failed_to_load_geography_details'));
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch parent geographies for dropdown
  const fetchParentOptions = async () => {
    try {
      // Get all geographies for parent selection
      const response = await geoService.getGeographies();
      
      // Extract geographies from the response
      const geographiesData = response.geography_entries || 
                            (response.data && response.data.geography_entries) || 
                            [];
      
      setParentOptions([
        { geo_id: 'NONE', geo_name: t('no_parent') },
        ...geographiesData
      ]);
    } catch (err) {
      console.error('Error fetching parent options:', err);
    }
  };
  
  // Initialize map with GeoJSON data
  const initializeMap = (geoJson) => {
    if (!mapContainer.current) return;
    
    // Remove existing map if any
    if (map.current) {
      map.current.remove();
    }
    
    // Create new map
    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [0, 0],
      zoom: 1
    });
    
    newMap.on('load', () => {
      // Add navigation controls
      newMap.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      // Add GeoJSON source
      newMap.addSource('geography', {
        type: 'geojson',
        data: geoJson
      });
      
      // Add fill layer
      newMap.addLayer({
        id: 'geography-fill',
        type: 'fill',
        source: 'geography',
        paint: {
          'fill-color': '#4a6cf7',
          'fill-opacity': 0.4
        }
      });
      
      // Add outline layer
      newMap.addLayer({
        id: 'geography-outline',
        type: 'line',
        source: 'geography',
        paint: {
          'line-color': '#000',
          'line-width': 1
        }
      });
      
      // Fit map to GeoJSON bounds
      const bounds = new mapboxgl.LngLatBounds();
      
      // Handle different GeoJSON types
      if (geoJson.type === 'FeatureCollection') {
        geoJson.features.forEach(feature => {
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates[0].forEach(coord => {
                bounds.extend(coord);
              });
            } else if (feature.geometry.type === 'MultiPolygon') {
              feature.geometry.coordinates.forEach(polygon => {
                polygon[0].forEach(coord => {
                  bounds.extend(coord);
                });
              });
            }
          }
        });
      } else if (geoJson.type === 'Feature') {
        if (geoJson.geometry && geoJson.geometry.coordinates) {
          if (geoJson.geometry.type === 'Polygon') {
            geoJson.geometry.coordinates[0].forEach(coord => {
              bounds.extend(coord);
            });
          } else if (geoJson.geometry.type === 'MultiPolygon') {
            geoJson.geometry.coordinates.forEach(polygon => {
              polygon[0].forEach(coord => {
                bounds.extend(coord);
              });
            });
          }
        }
      }
      
      // Only fit bounds if we have coordinates
      if (!bounds.isEmpty()) {
        newMap.fitBounds(bounds, {
          padding: 50
        });
      }
    });
    
    map.current = newMap;
  };
  
  // Initial fetch of geographies
  useEffect(() => {
    if (selectedGeoType) {
      fetchGeographiesByType(selectedGeoType);
    }
    
    fetchParentOptions();
  }, [selectedGeoType]);
  
  // Filter geographies based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredGeographies(geographies);
      return;
    }
    
    const filtered = geographies.filter(geo => 
      geo.geo_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      geo.geo_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (geo.geo_id && geo.geo_id.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredGeographies(filtered);
  }, [searchTerm, geographies]);
  
  const handleGeoTypeSelect = (type) => {
    setSelectedGeoType(type);
    setSelectedGeography(null);
    setChildGeographies([]);
    setSearchTerm('');
    setNextToken(null);
    setHasMoreData(true);
  };
  
  const handleSelectGeography = (geo) => {
    if (geo.geo_id === selectedGeography?.geo_id) {
      return;
    }
    
    setSelectedGeography(null);
    setChildGeographies([]);
    setEditingGeography(false);
    fetchGeographyDetails(geo.geo_id);
  };
  
  const handleSearch = (term) => {
    setSearchTerm(term);
  };
  
  const handleRefresh = () => {
    setSearchTerm('');
    setNextToken(null);
    setHasMoreData(true);
    fetchGeographiesByType(selectedGeoType, null, false);
    fetchParentOptions();
  };
  
  const handleLoadMore = () => {
    if (nextToken && !isLoadingMore) {
      fetchGeographiesByType(selectedGeoType, nextToken, true);
    }
  };
  
  const handleNewGeography = () => {
    setEditingGeography(true);
    setSelectedGeography(null);
    setChildGeographies([]);
    setGeoJsonFile(null);
    setGeoJsonPreview(null);
    setNewGeography({
      geo_id: '',
      geo_type: selectedGeoType || '',
      geo_name: '',
      geo_name_fr: '',
      geo_code: '',
      parent_geo_id: 'NONE',
      properties: {}
    });
  };
  
  const handleEditGeography = () => {
    if (!selectedGeography) return;
    
    setEditingGeography(true);
    setGeoJsonFile(null);
    setGeoJsonPreview(null);
    setNewGeography({
      geo_id: selectedGeography.geo_id,
      geo_type: selectedGeography.geo_type,
      geo_name: selectedGeography.geo_name,
      geo_name_fr: selectedGeography.geo_name_fr || '',
      geo_code: selectedGeography.geo_code,
      parent_geo_id: selectedGeography.parent_geo_id || 'NONE',
      properties: selectedGeography.properties || {}
    });
  };
  
  const handleGeographyChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('properties.')) {
      const propertyName = name.split('.')[1];
      setNewGeography(prev => ({
        ...prev,
        properties: {
          ...prev.properties,
          [propertyName]: value
        }
      }));
    } else {
      setNewGeography(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setGeoJsonFile(file);
    
    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        const result = geoService.validateGeoJSON(content);
        
        if (result.valid) {
          setGeoJsonPreview(result.geoJson);
          setError(null);
        } else {
          setGeoJsonPreview(null);
          setError(t('invalid_geojson_file') + ': ' + result.error);
        }
      } catch (err) {
        setGeoJsonPreview(null);
        setError(t('invalid_geojson_file'));
      }
    };
    reader.readAsText(file);
  };
  
  const handleSaveGeography = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate required fields
      if (!newGeography.geo_id || !newGeography.geo_type || !newGeography.geo_name || !newGeography.geo_code) {
        throw new Error(t('required_fields_missing'));
      }
      
      // Prepare data for API
      const geoData = {
        ...newGeography
      };
      
      // Add GeoJSON if available
      if (geoJsonPreview) {
        geoData.geometry_boundary = geoJsonPreview;
      }
      
      let response;
      
      if (selectedGeography) {
        // Update existing geography
        response = await geoService.updateGeography(geoData);
        setSuccess(t('geography_updated_successfully'));
      } else {
        // Create new geography
        response = await geoService.createGeography(geoData);
        setSuccess(t('geography_created_successfully'));
      }
      
      // Refresh the list
      fetchGeographiesByType(selectedGeoType);
      fetchParentOptions();
      
      // Select the newly created/updated geography
      const updatedGeo = response.data || response;
      if (updatedGeo) {
        fetchGeographyDetails(updatedGeo.geo_id);
      }
      
      setEditingGeography(false);
    } catch (err) {
      console.error('Error saving geography:', err);
      setError(err.message || t('failed_to_save_geography'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteGeography = async () => {
    if (!selectedGeography) return;
    
    if (!window.confirm(t('confirm_delete_geography'))) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await geoService.deleteGeography(selectedGeography.geo_id);
      
      // Refresh the list
      fetchGeographiesByType(selectedGeoType);
      
      setSelectedGeography(null);
      setChildGeographies([]);
      setSuccess(t('geography_deleted_successfully'));
    } catch (err) {
      console.error('Error deleting geography:', err);
      setError(t('failed_to_delete_geography'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  const toggleMap = () => {
    setShowMap(!showMap);
  };
  
  const formatDate = geoService.formatDate;
  
  return (
    <div className={styles.geographyTab}>
      <div className={styles.geographyHeader}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('search_geographies')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedGeoType}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearchButton}
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>
        <button 
          className={styles.uploadButton}
          onClick={() => setShowUploadModal(true)}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faUpload} />
          <span>{t('batch_upload')}</span>
        </button>
        <button 
          className={styles.refreshButton}
          onClick={handleRefresh}
          disabled={isRefreshing || !selectedGeoType}
        >
          <FontAwesomeIcon icon={faSync} spin={isRefreshing} />
          <span>{isRefreshing ? t('refreshing') : t('refresh')}</span>
        </button>
      </div>
      
      <div className={styles.geographyContent}>
        <div className={styles.geographySidebar}>
          <div className={styles.geoTypesList}>
            <div className={styles.geoTypesHeader}>
              <h3 className={styles.geoTypesTitle}>{t('geography_types')}</h3>
            </div>
            <div className={styles.geoTypesItems}>
              {geoTypes.map(type => (
                <div
                  key={type}
                  className={`${styles.geoTypeItem} ${selectedGeoType === type ? styles.selectedGeoType : ''}`}
                  onClick={() => handleGeoTypeSelect(type)}
                >
                  <FontAwesomeIcon icon={faGlobe} className={styles.geoTypeIcon} />
                  <span className={styles.geoTypeName}>{t(`geo_type_${type}`)}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.geographiesList}>
            <div className={styles.geographiesHeader}>
              <h3 className={styles.geographiesTitle}>
                {selectedGeoType ? t(`geo_type_${selectedGeoType}_plural`) : t('geographies')}
              </h3>
              {selectedGeoType && (
                <button
                  className={styles.addGeographyButton}
                  onClick={handleNewGeography}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              )}
            </div>
            
            {loading && !editingGeography && !selectedGeography ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>{t('loading_geographies')}</p>
              </div>
            ) : selectedGeoType && filteredGeographies.length > 0 ? (
              <div className={styles.geographiesItems}>
                <div className={styles.geographiesListHeader}>
                  <div className={styles.geographyItemRow}>
                    <span className={styles.geographyItemNumber}>#</span>
                    <span className={styles.geographyItemCode}>{t('code')}</span>
                    <span className={styles.geographyItemName}>{t('name')}</span>
                  </div>
                </div>
                {filteredGeographies.map((geo, index) => (
                  <div
                    key={geo.geo_id}
                    className={`${styles.geographyItem} ${selectedGeography?.geo_id === geo.geo_id ? styles.selectedGeography : ''}`}
                    onClick={() => handleSelectGeography(geo)}
                  >
                    <div className={styles.geographyItemRow}>
                      <span className={styles.geographyItemNumber}>{index + 1}</span>
                      <span className={styles.geographyItemCode}>{geo.geo_code}</span>
                      <span className={styles.geographyItemName}>{geo.geo_name}</span>
                    </div>
                  </div>
                ))}
                
                {hasMoreData && (
                  <div className={styles.loadMoreContainer}>
                    <button 
                      className={styles.loadMoreButton}
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? (
                        <>
                          <div className={styles.spinner}></div>
                          <span>{t('loading_more')}</span>
                        </>
                      ) : (
                        <span>{t('load_more')}</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : selectedGeoType ? (
              <div className={styles.emptyState}>
                <p>{searchTerm ? t('no_matching_geographies') : t('no_geographies_found')}</p>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>{t('select_geography_type')}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.geographyMain}>
          {editingGeography ? (
            <div className={styles.geographyForm}>
              <h3 className={styles.formTitle}>
                {selectedGeography ? t('edit_geography') : t('add_geography')}
              </h3>
              
              <div className={styles.formGrid}>
                <div className={styles.formColumn}>
                  <div className={styles.formGroup}>
                    <label htmlFor="geo_id" className={styles.formLabel}>
                      {t('geo_id')} <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="geo_id"
                      name="geo_id"
                      className={styles.formInput}
                      value={newGeography.geo_id}
                      onChange={handleGeographyChange}
                      disabled={loading || selectedGeography}
                      required
                    />
                    <div className={styles.formHelp}>{t('geo_id_help')}</div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="geo_type" className={styles.formLabel}>
                      {t('geo_type')} <span className={styles.required}>*</span>
                    </label>
                    <select
                      id="geo_type"
                      name="geo_type"
                      className={styles.formSelect}
                      value={newGeography.geo_type}
                      onChange={handleGeographyChange}
                      disabled={loading}
                      required
                    >
                      <option value="">{t('select_geo_type')}</option>
                      {geoTypes.map(type => (
                        <option key={type} value={type}>{t(`geo_type_${type}`)}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="geo_name" className={styles.formLabel}>
                      {t('geo_name')} <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="geo_name"
                      name="geo_name"
                      className={styles.formInput}
                      value={newGeography.geo_name}
                      onChange={handleGeographyChange}
                      disabled={loading}
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="geo_name_fr" className={styles.formLabel}>
                      {t('geo_name_fr')}
                    </label>
                    <input
                      type="text"
                      id="geo_name_fr"
                      name="geo_name_fr"
                      className={styles.formInput}
                      value={newGeography.geo_name_fr}
                      onChange={handleGeographyChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className={styles.formColumn}>
                  <div className={styles.formGroup}>
                    <label htmlFor="geo_code" className={styles.formLabel}>
                      {t('geo_code')} <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="geo_code"
                      name="geo_code"
                      className={styles.formInput}
                      value={newGeography.geo_code}
                      onChange={handleGeographyChange}
                      disabled={loading}
                      required
                    />
                    <div className={styles.formHelp}>{t('geo_code_help')}</div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="parent_geo_id" className={styles.formLabel}>
                      {t('parent_geography')}
                    </label>
                    <select
                      id="parent_geo_id"
                      name="parent_geo_id"
                      className={styles.formSelect}
                      value={newGeography.parent_geo_id}
                      onChange={handleGeographyChange}
                      disabled={loading}
                    >
                      {parentOptions.map(option => (
                        <option key={option.geo_id} value={option.geo_id}>
                          {option.geo_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      {t('geometry_boundary')}
                    </label>
                    <div className={styles.fileUploadContainer}>
                      <input
                        type="file"
                        id="geometry_boundary"
                        accept=".json,.geojson"
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        disabled={loading}
                      />
                      <label htmlFor="geometry_boundary" className={styles.fileUploadButton}>
                        <FontAwesomeIcon icon={faUpload} />
                        <span>{geoJsonFile ? geoJsonFile.name : t('upload_geojson')}</span>
                      </label>
                    </div>
                    {geoJsonPreview && (
                      <div className={styles.geoJsonPreview}>
                        <div className={styles.geoJsonPreviewHeader}>
                          <span>{t('geojson_preview')}</span>
                          <span className={styles.geoJsonPreviewType}>{geoJsonPreview.type}</span>
                        </div>
                        <div className={styles.geoJsonPreviewContent}>
                          {JSON.stringify(geoJsonPreview).substring(0, 100)}...
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('properties')}
                </label>
                <div className={styles.propertiesContainer}>
                  <div className={styles.propertyRow}>
                    <input
                      type="text"
                      placeholder={t('property_key')}
                      className={styles.propertyKeyInput}
                      disabled={true}
                      value="population"
                    />
                    <input
                      type="text"
                      name="properties.population"
                      placeholder={t('property_value')}
                      className={styles.propertyValueInput}
                      value={newGeography.properties?.population || ''}
                      onChange={handleGeographyChange}
                      disabled={loading}
                    />
                  </div>
                  <div className={styles.propertyRow}>
                    <input
                      type="text"
                      placeholder={t('property_key')}
                      className={styles.propertyKeyInput}
                      disabled={true}
                      value="area"
                    />
                    <input
                      type="text"
                      name="properties.area"
                      placeholder={t('property_value')}
                      className={styles.propertyValueInput}
                      value={newGeography.properties?.area || ''}
                      onChange={handleGeographyChange}
                      disabled={loading}
                    />
                  </div>
                  <div className={styles.propertyRow}>
                    <input
                      type="text"
                      placeholder={t('property_key')}
                      className={styles.propertyKeyInput}
                      disabled={true}
                      value="source"
                    />
                    <input
                      type="text"
                      name="properties.source"
                      placeholder={t('property_value')}
                      className={styles.propertyValueInput}
                      value={newGeography.properties?.source || ''}
                      onChange={handleGeographyChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setEditingGeography(false)}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={handleSaveGeography}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className={styles.spinner}></div>
                      <span>{t('saving')}</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEdit} />
                      <span>{t('save')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : selectedGeography ? (
            <div className={styles.geographyDetails}>
              <div className={styles.geographyHeader}>
                <div className={styles.geographyInfo}>
                  <div className={styles.geographyIdBadge}>
                    ID: {selectedGeography.geo_id}
                  </div>
                  <h3 className={styles.geographyName}>{selectedGeography.geo_name}</h3>
                  {selectedGeography.geo_name_fr && (
                    <p className={styles.geographyNameFr}>{selectedGeography.geo_name_fr}</p>
                  )}
                  <div className={styles.geographyMeta}>
                    <span className={styles.geographyType}>
                      {t(`geo_type_${selectedGeography.geo_type}`)}
                    </span>
                    <span className={styles.geographyCode}>
                      {t('code')}: {selectedGeography.geo_code}
                    </span>
                  </div>
                </div>
                <div className={styles.geographyActions}>
                  <button
                    className={styles.editButton}
                    onClick={handleEditGeography}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    <span>{t('edit')}</span>
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={handleDeleteGeography}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                    <span>{t('delete')}</span>
                  </button>
                </div>
              </div>
              
              <div className={styles.geographyDetailsContent}>
                <div className={styles.geographyDetailsSection}>
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionTitle}>{t('details')}</h4>
                  </div>
                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t('geo_id')}</div>
                      <div className={styles.detailValue}>{selectedGeography.geo_id}</div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t('geo_type')}</div>
                      <div className={styles.detailValue}>{t(`geo_type_${selectedGeography.geo_type}`)}</div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t('geo_code')}</div>
                      <div className={styles.detailValue}>{selectedGeography.geo_code}</div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t('parent_geography')}</div>
                      <div className={styles.detailValue}>
                        {selectedGeography.parent_geo_id === 'NONE' 
                          ? t('no_parent') 
                          : selectedGeography.parent_geo_id}
                      </div>
                    </div>
                  </div>
                </div>
                
                {selectedGeography.properties && Object.keys(selectedGeography.properties).length > 0 && (
                  <div className={styles.geographyDetailsSection}>
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>{t('properties')}</h4>
                    </div>
                    <div className={styles.propertiesGrid}>
                      {Object.entries(selectedGeography.properties).map(([key, value]) => (
                        <div key={key} className={styles.propertyItem}>
                          <div className={styles.propertyLabel}>{key}</div>
                          <div className={styles.propertyValue}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedGeography.geometry_boundary && (
                  <div className={styles.geographyDetailsSection}>
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>{t('boundary')}</h4>
                      <button 
                        className={styles.toggleMapButton}
                        onClick={toggleMap}
                      >
                        <FontAwesomeIcon icon={faEye} />
                        <span>{showMap ? t('hide_map') : t('show_map')}</span>
                      </button>
                    </div>
                    {showMap && (
                      <div className={styles.mapContainer} ref={mapContainer}></div>
                    )}
                    <div className={styles.boundaryInfo}>
                      <div className={styles.boundaryLink}>
                        <FontAwesomeIcon icon={faLink} />
                        <span>{selectedGeography.geometry_boundary}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {childGeographies.length > 0 && (
                  <div className={styles.geographyDetailsSection}>
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>{t('child_geographies')}</h4>
                      <span className={styles.childCount}>
                        {childGeographies.length} {childGeographies.length === 1 
                          ? t('child') 
                          : t('children')}
                      </span>
                    </div>
                    <div className={styles.childrenList}>
                      <div className={styles.childrenListHeader}>
                        <div className={styles.childRow}>
                          <span className={styles.childNumber}>#</span>
                          <span className={styles.childId}>{t('geo_id')}</span>
                          <span className={styles.childCode}>{t('code')}</span>
                          <span className={styles.childName}>{t('name')}</span>
                          <span className={styles.childType}>{t('type')}</span>
                        </div>
                      </div>
                      <div className={styles.childrenListBody}>
                        {childGeographies.map((child, index) => (
                          <div 
                            key={child.geo_id} 
                            className={styles.childRow}
                            onClick={() => handleSelectGeography(child)}
                          >
                            <span className={styles.childNumber}>{index + 1}</span>
                            <span className={styles.childId}>{child.geo_id}</span>
                            <span className={styles.childCode}>{child.geo_code}</span>
                            <span className={styles.childName}>{child.geo_name}</span>
                            <span className={styles.childType}>{t(`geo_type_${child.geo_type}`)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className={styles.geographyDetailsSection}>
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionTitle}>{t('metadata')}</h4>
                  </div>
                  <div className={styles.metadataGrid}>
                    <div className={styles.metadataItem}>
                      <div className={styles.metadataLabel}>{t('created_at')}</div>
                      <div className={styles.metadataValue}>{formatDate(selectedGeography.created_at)}</div>
                    </div>
                    <div className={styles.metadataItem}>
                      <div className={styles.metadataLabel}>{t('updated_at')}</div>
                      <div className={styles.metadataValue}>{formatDate(selectedGeography.updated_at)}</div>
                    </div>
                    {selectedGeography.created_by && (
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>{t('created_by')}</div>
                        <div className={styles.metadataValue}>{selectedGeography.created_by}</div>
                      </div>
                    )}
                    {selectedGeography.updated_by && (
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>{t('updated_by')}</div>
                        <div className={styles.metadataValue}>{selectedGeography.updated_by}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.noSelectionState}>
              <FontAwesomeIcon icon={faGlobe} size="2x" />
              <p>{selectedGeoType 
                ? t('select_geography_or_create_new') 
                : t('select_geography_type_first')}
              </p>
            </div>
          )}
        </div>
      </div>
      {showUploadModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{t('batch_upload_geographies')}</h3>
              <button 
                className={styles.modalClose}
                onClick={() => setShowUploadModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <GeographyUpload onUploadComplete={handleUploadComplete} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeographyManagement;